import React, { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { Box, CircularProgress } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { useTranslation } from 'react-i18next'
import { mapLocation } from 'lib/carDetailsLib'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'
import config from 'config'

import PageLoading from 'components/atoms/PageLoading'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import CarsList from 'components/atoms/CarsList'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useLogs from 'hooks/useLogs'
import useInputOutput from 'hooks/useInputOutput'

import useCars from '../Sessions/useCars'

import CarDetails from './CarDetails'

export default function SessionsConfirmation({
  session,
  handleValidation,
  handleConfirm,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
  setShowHeader,
  debounceTime = 100,
}) {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  const [filteredCars, setFilteredCars] = useState([])

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { isFetching, fetchCars, cars } = useCars()
  const [showDetails, setShowDetails] = useState()

  useEffect(() => {
    setShowHeader(!showDetails)
  }, [setShowHeader, showDetails])

  useEffect(() => {
    fetchCars(session.sessionId)
  }, [fetchCars, session])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setYearRange,
    setBrands,
    setModels,
    setFuels,
    setQuery,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [fetchCars, cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    debounceTime
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const { saveXls } = useInputOutput()

  const handleDownload = async () => {
    const titlesRow = [
      t(`common.excel.${countryConfig.attribute.primary}`),
      t('sessions.price'),
    ]

    titlesRow.push(
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.optionals')
    )

    const rows = [
      titlesRow,
      ...cars.map((car) => [
        car[countryConfig.attribute.primary],
        car.prices?.[1],

        `${car.title}`,
        car.km,
        mapLocation(car.location),
        t(`carDetails.mapping.${car.transmission}`),
        car.reconditioningEstCost || '-',
        car.year,
        car.fuelType,
        Math.round(car.optionAmount) || '-',
      ]),
    ]

    saveXls('confirmation', rows)
  }

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  if (showDetails) {
    return (
      <CarDetails
        cars={filteredCars}
        vin={showDetails}
        setShowDetails={setShowDetails}
      />
    )
  }

  return (
    <div data-testid="session-confirmation">
      <Box mt={1} />
      <ConfirmBar
        label={t(`sessions.confirmation.confirmButton`)}
        left={
          <SearchField
            data-testid="text-search"
            placeholder={t('common.form.searchBy')}
            value={filters.query}
            onChange={handleChangeSearch}
          />
        }
        leftProps={{ sx: { width: 0.5 } }}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_sessions_${session?.sessionId}`, {
                  area: t('common.logs.sessions'),
                  sessionId: session?.sessionId,
                })
              }
            />

            <DownloadBar handleDownload={handleDownload} />
          </>
        }
        disabled={
          !handleValidation() || confirmed || readOnly || contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
        sticky
      />
      <CarsList
        left={
          <CarFiltersColumn
            filters={filters}
            filtersData={filtersData}
            filteredCars={
              filteredCars?.length !== cars.length ? filteredCars : undefined
            }
            setKmRange={setKmRange}
            setYearRange={setYearRange}
            setBrands={setBrands}
            setModels={setModels}
            setFuels={setFuels}
            resetFilters={resetFilters}
          />
        }
        right={
          <>
            {!isFetching ? (
              filteredCars.map((car, index) => (
                <CarCard
                  data-testid={`car-${index}`}
                  key={car.originalId}
                  onClick={() => setShowDetails(car.vin)}
                  car={car}
                  price={car.prices?.[1]}
                  fee={car.fee}
                />
              ))
            ) : (
              <PageLoading />
            )}
          </>
        }
      />
      <ConfirmationDialog
        title={t('sessions.confirmation.confirmationDialog.title')}
        content={
          <>
            {t('sessions.confirmation.confirmationDialog.content')}
            <strong>
              {session.agenda?.publication.startsAt &&
                format(
                  new Date(session.agenda.publication.startsAt),
                  t('common.date.format'),
                  {
                    locale: itLocale,
                  }
                )}
            </strong>
          </>
        }
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </div>
  )
}

SessionsConfirmation.propTypes = {
  session: T.object.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
